<template>
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        :nudge-width="280"
        offset-y
        left
        :max-width="320"
    >
        <template v-slot:activator="{ on, attrs }">
            <slot name="activator" :on="on" :attrs="attrs">
                <v-avatar
                    :size="avatarSize"
                    :color="avatarColor"
                    class="cursor-pointer"
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-img v-if="teacher && teacher.photo" :src="teacher.photo" />
                    <v-icon v-else :color="iconColor">mdi-account</v-icon>
                </v-avatar>
            </slot>
        </template>

        <v-card class="teacher-quick-view">
            <template v-if="loading">
                <v-card-text>
                    <v-skeleton-loader type="list-item-avatar-two-line, divider, list-item-two-line" />
                </v-card-text>
            </template>

            <template v-else-if="teacherData">
                <v-card-title class="pb-0">
                    <v-avatar size="48" color="grey lighten-2" class="mr-3">
                        <v-img v-if="teacherData.photo" :src="teacherData.photo" />
                        <v-icon v-else color="grey">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                        <div class="subtitle-1 font-weight-medium">{{ teacherData.name }}</div>
                        <div class="caption grey--text" v-if="teacherData.registration">
                            Matrícula: {{ teacherData.registration }}
                        </div>
                    </div>
                </v-card-title>

                <v-card-text class="pt-2">
                    <v-chip
                        v-if="teacherData.status"
                        x-small
                        :color="teacherData.status.active ? 'success' : 'grey'"
                        dark
                        class="mb-2"
                    >
                        {{ teacherData.status.name || 'Ativo' }}
                    </v-chip>

                    <v-divider class="my-2" />

                    <v-list dense class="pa-0">
                        <v-list-item v-if="teacherData.email" class="px-0" @click="copyToClipboard(teacherData.email)">
                            <v-list-item-icon class="mr-2">
                                <v-icon small color="grey">mdi-email</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="body-2">{{ teacherData.email }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon x-small color="grey lighten-1">mdi-content-copy</v-icon>
                            </v-list-item-action>
                        </v-list-item>

                        <v-list-item v-if="teacherData.mobile_phone" class="px-0" @click="copyToClipboard(teacherData.mobile_phone)">
                            <v-list-item-icon class="mr-2">
                                <v-icon small color="grey">mdi-phone</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="body-2">{{ teacherData.mobile_phone }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon x-small color="grey lighten-1">mdi-content-copy</v-icon>
                            </v-list-item-action>
                        </v-list-item>

                        <v-list-item v-if="teacherData.titling" class="px-0">
                            <v-list-item-icon class="mr-2">
                                <v-icon small color="grey">mdi-school</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="body-2">{{ teacherData.titling.name }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>

                    <template v-if="teacherData.disciplines && teacherData.disciplines.length">
                        <v-divider class="my-2" />
                        <div class="caption grey--text mb-1">Disciplinas</div>
                        <v-chip-group column>
                            <v-chip
                                v-for="discipline in teacherData.disciplines.slice(0, 5)"
                                :key="discipline.id"
                                x-small
                                outlined
                            >
                                {{ discipline.name }}
                            </v-chip>
                            <v-chip
                                v-if="teacherData.disciplines.length > 5"
                                x-small
                                color="grey"
                                text-color="white"
                            >
                                +{{ teacherData.disciplines.length - 5 }}
                            </v-chip>
                        </v-chip-group>
                    </template>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        text
                        small
                        color="primary"
                        @click="viewFullProfile"
                    >
                        <v-icon small left>mdi-account-details</v-icon>
                        Ver Perfil Completo
                    </v-btn>
                    <v-spacer />
                    <v-btn icon small @click="menu = false">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </v-card-actions>
            </template>

            <template v-else>
                <v-card-text class="text-center py-4">
                    <v-icon color="grey lighten-1">mdi-account-off</v-icon>
                    <div class="caption grey--text mt-1">Informações não disponíveis</div>
                </v-card-text>
            </template>
        </v-card>

        <emc-alert-snackbar
            :show="!!snackbarMessage"
            :message="snackbarMessage"
            color="success"
            @update:show="snackbarMessage = ''"
        />
    </v-menu>
</template>

<script>
export default {
    name: "SysTeacherQuickView",

    data: () => ({
        menu: false,
        loading: false,
        teacherData: null,
        snackbarMessage: ''
    }),

    props: {
        teacher: {
            type: Object,
            default: null
        },
        teacherId: {
            type: [Number, String],
            default: null
        },
        avatarSize: {
            type: [Number, String],
            default: 32
        },
        avatarColor: {
            type: String,
            default: 'grey lighten-2'
        },
        iconColor: {
            type: String,
            default: 'grey'
        },
        fetchOnOpen: {
            type: Boolean,
            default: false
        }
    },

    watch: {
        menu(newVal) {
            if (newVal && this.fetchOnOpen && !this.teacherData) {
                this.fetchTeacherDetails()
            }
            if (newVal && this.teacher && !this.teacherData) {
                this.teacherData = this.teacher
            }
        },
        teacher: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.teacherData = newVal
                }
            }
        }
    },

    methods: {
        async fetchTeacherDetails() {
            const id = this.teacherId || this.teacher?.id
            if (!id) return

            this.loading = true
            try {
                const response = await this.$http.get(`/teachers/${id}`)
                this.teacherData = response.data
            } catch (error) {
                this.$emit('error', error)
            } finally {
                this.loading = false
            }
        },

        copyToClipboard(text) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text)
                this.snackbarMessage = 'Copiado para a área de transferência'
                setTimeout(() => {
                    this.snackbarMessage = ''
                }, 2000)
            }
        },

        viewFullProfile() {
            const id = this.teacherData?.id || this.teacherId || this.teacher?.id
            if (id) {
                this.$emit('click:profile', this.teacherData)
                this.menu = false
            }
        }
    }
}
</script>

<style scoped>
.teacher-quick-view {
    max-width: 320px;
}

.cursor-pointer {
    cursor: pointer;
}

.v-list-item {
    min-height: 36px !important;
}

.v-list-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
    cursor: pointer;
}
</style>
